/**
 * 入力画面の上半分（表示だけ）の計算（純関数。Issue #467）。
 *
 * 座標は**フィールド動画のピクセル**（world）。上半分は「world のどの矩形を見せるか」（表示）を選び、その矩形を上半分に contain で
 * 収める（はみ出さない）。動画・ゾーンの枠・core の盤面のカードは、同じ world の座標で重ねるので、表示を変えても揃ったまま動く。
 *
 * フィールド動画の縦の並び: P2 の手札の帯（180° 回転）/ P2 のフィールド / P1 のフィールド / P1 の手札の帯（utils/defaultOverlayZones.js の fieldCrop と同じ前提）。
 * **見せるのは動画の範囲だけ**。視点は切り替えない（上が P2・下が P1 で固定）。core の手札は 2 人ぶんをフィールドの 3 行目に出す（動画の手札の帯には重ねない）。
 * ゾーンは overlay_zones の位置のまま（墓地・除外を外に並べ直さない）。動画の横幅の外に出るゾーン（同梱マットの墓地など）は描かない。
 */
import { CARD_ASPECT } from './common';
import { fieldCrop } from '../../utils/defaultOverlayZones';
import { defaultOverlayZones } from '../../utils/defaultOverlayZones';

/** 表示の一覧（並び順どおりにボタンを出す） */
export const VIEWS = [
  { key: 'all', label: '全体' },
  { key: 'field', label: 'フィールド' },
  { key: 'hand0', label: 'P1 手札' },
  { key: 'hand1', label: 'P2 手札' },
  // ゲーム映像（描く側が演出を載せて描いた画面全体。補正サーバーの /live/stream?rec=…&fx=1。Issue #765）。ライブの録画があるときだけ出す（availableViews の fx）
  { key: 'fx', label: '演出' },
];

/** config の overlay_zones（{ video_width, video_height, zones }）を動画の寸法に合わせる。無ければ同梱マットの推定 */
export function resolveZones(config, videoSize) {
  if (!videoSize) return null;
  const oz = config?.overlay_zones;
  const raw = oz?.zones ?? (oz && !oz.video_width ? oz : null);
  let zones;
  if (!raw) zones = defaultOverlayZones(videoSize.w, videoSize.h)?.zones ?? null;
  else {
    const kx = oz.video_width > 0 ? videoSize.w / oz.video_width : 1;
    const ky = oz.video_height > 0 ? videoSize.h / oz.video_height : 1;
    zones = Object.fromEntries(Object.entries(raw).map(([k, r]) => [k, { x: r.x * kx, y: r.y * ky, w: r.w * kx, h: r.h * ky }]));
  }
  return zones ?? null;
}

/** 上半分に描かないゾーン（除外は入力に要らない。ユーザー判断） */
export const HIDDEN_ZONES = ['removed'];

/** 動画の範囲に中心があるゾーンだけ（外に出るゾーンと HIDDEN_ZONES は描かない） */
export function visibleZones(zones, videoSize) {
  if (!zones || !videoSize) return {};
  return Object.fromEntries(Object.entries(zones).filter(([key, r]) => finiteRect(r) && !HIDDEN_ZONES.includes(key.split(':')[0])
    && r.x + r.w / 2 >= 0 && r.x + r.w / 2 <= videoSize.w && r.y + r.h / 2 >= 0 && r.y + r.h / 2 <= videoSize.h));
}

const finiteRect = (r) => [r?.x, r?.y, r?.w, r?.h].every(Number.isFinite);

/** 手札の帯（world の矩形）。P1 = 下、P2 = 上。帯が無ければ null */
export function handBandRect(videoSize, player) {
  const crop = fieldCrop(videoSize);
  if (!crop || !(crop.hand > 0)) return null;
  return player === 1 ? { x: 0, y: 0, w: videoSize.w, h: crop.y0 } : { x: 0, y: crop.y1, w: videoSize.w, h: videoSize.h - crop.y1 };
}

/**
 * 表示の矩形: { x, y, w, h, rotate }（rotate は度。P2 の手札は動画の中で逆さなので 180° 回して読める向きにする）。
 * seat（席モードの自分の席 0 / 1）を渡すと、全体は相手の手札の帯を除く（相手の手札は「相手の手札」の表示で見る）。
 * 出せない表示（手札の帯が無い・動画の寸法が無い）は null
 */
export function viewRect(view, videoSize, { seat = null } = {}) {
  if (!(videoSize?.w > 0) || !(videoSize?.h > 0)) return null;
  // 演出（ゲーム映像）は描く側が組んだ画面そのものなので丸ごと（盤面・ゾーンは重ねない）
  if (view === 'fx') return { x: 0, y: 0, w: videoSize.w, h: videoSize.h, rotate: 0 };
  if (view === 'all') {
    const crop = (seat === 0 || seat === 1) ? fieldCrop(videoSize) : null;
    if (crop?.hand > 0) {
      return seat === 0 ? { x: 0, y: crop.y0, w: videoSize.w, h: videoSize.h - crop.y0, rotate: 0 } : { x: 0, y: 0, w: videoSize.w, h: crop.y1, rotate: 0 };
    }
    return { x: 0, y: 0, w: videoSize.w, h: videoSize.h, rotate: 0 };
  }
  if (view === 'field') {
    const crop = fieldCrop(videoSize);
    if (!crop || !(crop.hand > 0)) return null;   // 手札の帯が無ければ全体と同じなので出さない
    return { x: 0, y: crop.y0, w: videoSize.w, h: crop.y1 - crop.y0, rotate: 0 };
  }
  if (view === 'hand0' || view === 'hand1') {
    const player = view === 'hand1' ? 1 : 0;
    const band = handBandRect(videoSize, player);
    return band ? { ...band, rotate: player === 1 ? 180 : 0 } : null;
  }
  return null;
}

/** 出せる表示の鍵。fx（演出）は呼ぶ側がゲーム映像を出せると言ったときだけ（ライブの録画があるとき） */
export const availableViews = (videoSize, { fx = false } = {}) => VIEWS.filter((v) => (v.key === 'fx' ? fx : !!viewRect(v.key, videoSize))).map((v) => v.key);

/**
 * 矩形を箱（上半分）に contain で収める CSS transform（transform-origin は 0 0）。
 * world の点 p → 箱の中心 + scale × 回転 ×（p − 矩形の中心）。{ scale, transform }。材料が無ければ null
 */
export function fitTransform(rect, box) {
  if (!rect || !(rect.w > 0) || !(rect.h > 0) || !(box?.w > 0) || !(box?.h > 0)) return null;
  const scale = Math.min(box.w / rect.w, box.h / rect.h);
  const cx = rect.x + rect.w / 2; const cy = rect.y + rect.h / 2;
  const transform = `translate(${box.w / 2}px, ${box.h / 2}px) scale(${scale}) rotate(${rect.rotate ?? 0}deg) translate(${-cx}px, ${-cy}px)`;
  return { scale, transform };
}

/** ゾーンの矩形の中に置くカードの矩形（world）。守備表示は横向き（幅と高さを入れ替えた外形）。scale はゾーンに対する大きさ */
export function cardRectIn(zone, { defense = false, scale = 0.9 } = {}) {
  if (!finiteRect(zone)) return null;
  let h = zone.h * scale; let w = h * CARD_ASPECT;
  if (w > zone.w * scale) { w = zone.w * scale; h = w / CARD_ASPECT; }
  const cx = zone.x + zone.w / 2; const cy = zone.y + zone.h / 2;
  // 回転前の外形（描くときに中心で 90° 回す）
  return { x: cx - w / 2, y: cy - h / 2, w, h, defense };
}

/** 表側のモンスターのステータス（旧シミュレータの CardSlot と同じ 3 つ）: { level, atk, def }。レベルの無いもの（リンク）は level 0 */
export function monsterStats(card) {
  if (!card) return null;
  return { level: Number(card.current_level) || 0, atk: card.current_attack ?? null, def: card.current_defense ?? null };
}

const boardKey = (card) => (card ? `${card.controller ?? 0}:${card.location}:${card.pos}` : null);
const PILES = [['maindeck', false], ['extra', false], ['grave', true], ['removed', true]];

/**
 * core の盤面（OcgGetState の board。duel_info 互換）→ world に置くカードの一覧。
 * [{ key, code, faceUp, defense, owner, rect, zone?, count?, stats?, hand? }]（stats は表側のモンスターの { level, atk, def }）。山は一番上の 1 枚と枚数、手札はフィールドの 3 行目（handRowRect）に横に並べる
 */
export function boardSprites(board, allZones, videoSize) {
  if (!board || !allZones) return [];
  const zones = videoSize ? visibleZones(allZones, videoSize) : allZones;
  const out = [];
  const put = (card, zone, { faceUp, defense = false, count = null, stats = null } = {}) => {
    const rect = card && zone ? cardRectIn(zone, { defense }) : null;
    if (!rect) return;
    out.push({ key: boardKey(card), code: card.card_id != null ? String(card.card_id) : null, faceUp, defense, owner: card.controller ?? 0, rect, count, stats, zone });
  };
  [0, 1].forEach((p) => {
    (board.monster_field?.[p] ?? []).forEach((card, i) => {
      if (!card || i >= 5) return;
      const faceUp = card.is_face_up !== false;
      put(card, zones[`monster:${p}:${i + 1}`], { faceUp, defense: card.battle_pos !== 'attack', stats: faceUp ? monsterStats(card) : null });
    });
    (board.magic_trap?.[p] ?? []).forEach((card, i) => { if (card) put(card, zones[i === 5 ? `field:${p}` : `magic_trap:${p}:${i + 1}`], { faceUp: card.is_face_up !== false }); });
    PILES.forEach(([zone, faceUp]) => {
      const pile = (board[zone]?.[p] ?? []).filter(Boolean);
      if (pile.length) put(pile[pile.length - 1], zones[`${zone}:${p}`], { faceUp: faceUp && pile[pile.length - 1].is_face_up !== false, count: pile.length });
    });
  });
  if (videoSize) {
    [0, 1].forEach((p) => {
      const row = handRowRect(allZones, videoSize, p);
      const hand = (board.hand?.[p] ?? []).filter(Boolean);
      if (!row || !hand.length) return;
      const h = row.h * 0.9; const w = h * CARD_ASPECT; const gap = w * 0.08;
      const step = hand.length > 1 ? Math.min(w + gap, (row.w - w) / (hand.length - 1)) : 0;
      const total = step * (hand.length - 1) + w;
      const x0 = row.x + (row.w - total) / 2;
      const y = row.y + (row.h - h) / 2;
      hand.forEach((card, i) => out.push({ key: boardKey(card), code: card.card_id != null ? String(card.card_id) : null, faceUp: true, defense: false, owner: p, hand: true, rect: { x: x0 + i * step, y, w, h } }));
    });
  }
  return out;
}

/**
 * core の手札を並べる行（world の矩形）: **フィールドの 3 行目**＝魔法罠の行よりそのプレイヤー側の、フィールドの端まで。
 * 動画の手札の帯には重ねない（重ねると実写の手札が見えなくなる）。横はモンスターの行の幅（除外ゾーンは描かないので避けない）。
 * 高さはゾーン 1 つぶんまで。材料が無ければ null
 */
export function handRowRect(zones, videoSize, player) {
  const crop = fieldCrop(videoSize);
  const pick = (kind) => [1, 2, 3, 4, 5].map((i) => zones?.[`${kind}:${player}:${i}`]).filter(finiteRect);
  const monsters = pick('monster'); const spells = pick('magic_trap');
  if (!crop || !monsters.length || !spells.length) return null;
  const gap = 6;
  const x0 = Math.min(...monsters.map((r) => r.x)); const x1 = Math.max(...monsters.map((r) => r.x + r.w));
  const y0 = player === 0 ? Math.max(...spells.map((r) => r.y + r.h)) + gap : crop.y0 + gap;
  const y1 = player === 0 ? crop.y1 - gap : Math.min(...spells.map((r) => r.y)) - gap;
  if (!(y1 > y0)) return null;
  const zoneH = Math.max(...monsters.map((r) => r.h));
  const h = Math.min(y1 - y0, zoneH);
  if (!(x1 > x0) || !(h > 0)) return null;
  // P1 は魔法罠の行に寄せ、P2 も魔法罠の行に寄せる（フィールドの端の余白は外側に残す）
  return { x: x0, y: player === 0 ? y0 : y1 - h, w: x1 - x0, h };
}

/** 置く場所の鍵（gameUi.placeKey = `p:monster_field:i` / `p:magic_trap:i`）→ ゾーンの矩形の鍵 */
export function zoneKeyOfPlaceKey(key) {
  const [p, loc, seq] = String(key).split(':');
  const i = Number(seq);
  if (loc === 'monster_field') return i < 5 ? `monster:${p}:${i + 1}` : null;
  if (loc === 'magic_trap') return i < 5 ? `magic_trap:${p}:${i + 1}` : (i === 5 ? `field:${p}` : null);
  return null;
}
